import { useNavigate } from 'react-router-dom';
import { Brain, Briefcase, Target, Lightbulb, FlaskConical, Heart, Globe, GraduationCap, Coins, MessageCircle, Feather, BookOpen } from 'lucide-react';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ErrorDisplay from '@/components/ui/ErrorDisplay';
import BackgroundDecoration from '@/components/ui/BackgroundDecoration';
import { useTopics } from '@/features/learning/hooks/useTopics';
import { useAuth } from '@/features/authentication/contexts/AuthContext';
import { analyticsService } from '@/services/analyticsService';

const OnboardingTopics = () => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const { topics, loading, error, refetch } = useTopics();

  // Icon mapping function (same as Library page)
  const iconForTopic = (name: string) => {
    const n = String(name || '').toLowerCase();
    if (n.includes('روان') || n.includes('psych')) return <Brain className="w-8 h-8 sm:w-9 sm:h-9" />;
    if (n.includes('کسب') || n.includes('بیز') || n.includes('business') || n.includes('مدیر') || n.includes('رهبری')) return <Briefcase className="w-8 h-8 sm:w-9 sm:h-9" />;
    if (n.includes('بازاری') || n.includes('مارکت') || n.includes('فروش') || n.includes('هدف')) return <Target className="w-8 h-8 sm:w-9 sm:h-9" />;
    if (n.includes('ایده') || n.includes('خلاق') || n.includes('نوآوری') || n.includes('innovation')) return <Lightbulb className="w-8 h-8 sm:w-9 sm:h-9" />;
    if (n.includes('علم') || n.includes('شیمی') || n.includes('فیزیک') || n.includes('science')) return <FlaskConical className="w-8 h-8 sm:w-9 sm:h-9" />;
    if (n.includes('سلامت') || n.includes('خانواده') || n.includes('روابط') || n.includes('health')) return <Heart className="w-8 h-8 sm:w-9 sm:h-9" />;
    if (n.includes('جهان') || n.includes('تاریخ') || n.includes('بین') || n.includes('world')) return <Globe className="w-8 h-8 sm:w-9 sm:h-9" />;
    if (n.includes('یادگیری') || n.includes('آموزش') || n.includes('تحصیل') || n.includes('education')) return <GraduationCap className="w-8 h-8 sm:w-9 sm:h-9" />;
    if (n.includes('مالی') || n.includes('اقتصاد') || n.includes('finance') || n.includes('money')) return <Coins className="w-8 h-8 sm:w-9 sm:h-9" />;
    if (n.includes('ارتباط') || n.includes('communication')) return <MessageCircle className="w-8 h-8 sm:w-9 sm:h-9" />;
    if (n.includes('داستان') || n.includes('رمان') || n.includes('fiction') || n.includes('novel')) return <Feather className="w-8 h-8 sm:w-9 sm:h-9" />;
    return <BookOpen className="w-8 h-8 sm:w-9 sm:h-9" />;
  };

  // Color mapping for each topic
  const colorForTopic = (index: number) => {
    const colors = [
      'from-pink-500 to-pink-600',      // صورتی
      'from-red-500 to-red-600',        // قرمز
      'from-green-500 to-green-600',    // سبز
      'from-blue-500 to-blue-600',      // آبی
      'from-yellow-500 to-yellow-600',  // زرد
      'from-purple-500 to-purple-600',  // بنفش
      'from-orange-500 to-orange-600',  // نارنجی
      'from-teal-500 to-teal-600',      // فیروزه‌ای
      'from-indigo-500 to-indigo-600',  // نیلی
      'from-cyan-500 to-cyan-600',      // آبی روشن
      'from-rose-500 to-rose-600',      // گلی
      'from-emerald-500 to-emerald-600',// زمردی
    ];
    return colors[index % colors.length];
  };

  const handleTopicSelect = (topic: any) => {
    console.log('🚀 [OnboardingTopics] Topic selected:', topic.topic_id);
    void analyticsService.logEvent('onboarding_topic_selected', {
      topic_id: String(topic.topic_id),
      topic_name: topic.topic_name,
    });
    
    // Save selected topic to sessionStorage (more secure, cleared on tab close)
    sessionStorage.setItem('onboarding_topic_id', String(topic.topic_id));
    sessionStorage.setItem('onboarding_topic_name', topic.topic_name);
    
    // Also save to AuthContext for registration payload
    updateUser({ topic_ids: [topic.topic_id] });
    
    // Check if we came from Discovery page due to missing topic error
    const skipBooks = sessionStorage.getItem('skip_books_selection');
    
    if (skipBooks === 'true') {
      // Clear the flag
      sessionStorage.removeItem('skip_books_selection');
      
      // Skip books and go directly to phone registration
      console.log('⏭️ [OnboardingTopics] Skipping books, going to phone');
      navigate('/onboarding/phone', { replace: false });
    } else {
      // Normal flow: go to books selection
      console.log('📚 [OnboardingTopics] Going to books selection');
      navigate(`/onboarding/books?topic=${encodeURIComponent(topic.topic_name)}`, {
        replace: false,
        state: { topicName: topic.topic_name },
      });
    }
  };

  return (
    <div
      className="min-h-screen bg-white flex flex-col px-5 sm:px-7 pb-12 relative overflow-hidden"
      style={{ paddingTop: 'calc(var(--onboarding-header-h, 110px) + 10px)' }}
      dir="rtl"
    >
      <div
        className="grid grid-cols-2 content-start gap-2.5 sm:gap-3 flex-1 pt-2 mx-auto"
        style={{ width: 'clamp(230px, 82vw, 300px)' }}
      >
        {loading ? (
          <div className="col-span-2 flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="col-span-2">
            <ErrorDisplay message={error || ''} onRetry={refetch} />
          </div>
        ) : (
          topics.map((topic, index) => (
            <button
              key={topic.topic_id}
              onClick={() => handleTopicSelect(topic)}
              style={{ animationDelay: `${index * 45}ms` }}
              className={`onboarding-topic-enter relative aspect-square w-full bg-gradient-to-br ${colorForTopic(index)} rounded-xl sm:rounded-2xl p-1.5 min-[380px]:p-2 sm:p-2.5 flex flex-col items-center justify-center gap-1 shadow-lg hover:scale-105 hover:shadow-xl active:scale-95 transition-all duration-300 overflow-hidden`}
            >
              {/* Icon */}
              <div className="text-white">
                {iconForTopic(topic.topic_name)}
              </div>
              
              {/* Topic Name */}
              <h3 className="text-white font-bold text-[11px] min-[380px]:text-xs sm:text-sm font-persian text-center leading-tight line-clamp-2 px-0.5">
                {topic.topic_name}
              </h3>
            </button>
          ))
        )}
      </div>

      <BackgroundDecoration />
    </div>
  );
};

export default OnboardingTopics;
